import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { adContracts as adContractsApi, media as mediaApi, clients as clientsApi, rentals as rentalsApi, type AdContract, type ClientFull, type UnattachedRental } from '../api/endpoints';
import type { Media } from '../types';

// Postgres DATE columns come back as ISO timestamps via pg-node
// (e.g. "2026-03-01T00:00:00.000Z"). <input type="date"> and the
// backend's YYYY-MM-DD regex both require the bare date part. This
// helper normalizes everywhere we hand a date to an input or to the
// API.
function toDateInput(s: string | null | undefined): string {
  return s ? s.slice(0, 10) : '';
}

/**
 * Admin detail page for a single ad contract. Shows:
 *   - Header: client + device + status pill
 *   - Term + billing card with editable auto_renew, end_date, amount, billing email
 *   - Ads list (rentals attached to this contract)
 *   - Renewal status (next-invoice date, renewal_invoice_id if minted)
 *
 * For `owner_perpetual` contracts the term/billing card is read-only — those
 * rows are managed via devices.owner_client_id on the parent device.
 */
export default function AdContractDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contract, setContract] = useState<AdContract | null>(null);
  const [client, setClient] = useState<ClientFull | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Editable form state — only used for 'rental' contracts.
  const [endDate, setEndDate] = useState('');
  const [amountDollars, setAmountDollars] = useState('');
  const [autoRenew, setAutoRenew] = useState(false);
  const [billingEmail, setBillingEmail] = useState('');
  const [notes, setNotes] = useState('');
  // Attach-existing-ad picker state.
  const [showAttach, setShowAttach] = useState(false);
  const [available, setAvailable] = useState<UnattachedRental[]>([]);
  const [attachBusy, setAttachBusy] = useState<string | null>(null); // rental id being attached
  // Attach-from-media-library picker state.
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaLib, setMediaLib] = useState<Media[]>([]);
  const [mediaSearch, setMediaSearch] = useState('');
  const [mediaBusy, setMediaBusy] = useState<string | null>(null); // media id being attached
  // Pre-program: dates applied to whatever gets attached next (defaults to contract window).
  const [pickerStart, setPickerStart] = useState('');
  const [pickerEnd, setPickerEnd] = useState('');
  /**
   * When set, the media picker is in "replace" mode for a specific rental
   * — clicking a thumbnail swaps that rental's media_id instead of
   * creating a new rental row. Cleared when the picker is opened in
   * attribution mode via the "+ Add from media library" button.
   */
  const [replaceTargetRentalId, setReplaceTargetRentalId] = useState<string | null>(null);
  // Inline date edit on the rentals table.
  const [editingRental, setEditingRental] = useState<string | null>(null);
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');

  useEffect(() => {
    if (!id) return;
    adContractsApi
      .get(id)
      .then((c) => {
        setContract(c);
        setEndDate(toDateInput(c.end_date));
        setAmountDollars(c.amount_cents != null ? (c.amount_cents / 100).toFixed(2) : '');
        setAutoRenew(c.auto_renew);
        setBillingEmail(c.billing_contact_email ?? '');
        setNotes(c.notes ?? '');
        // Hydrate the real client display name. Best-effort: a 404 or
        // shop-api hiccup leaves us with the "Client #N" placeholder.
        clientsApi.get(c.client_id).then(setClient).catch(() => setClient(null));
      })
      .catch((e) => setErr((e as Error).message));
  }, [id]);

  if (!contract) {
    return <div>{err ? <div className="error-banner">{err}</div> : 'Loading…'}</div>;
  }

  const isOwnerPerpetual = contract.contract_type === 'owner_perpetual';

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const amountCents = amountDollars.trim() === '' ? null : Math.round(parseFloat(amountDollars) * 100);
      const updated = await adContractsApi.update(contract.id, {
        endDate: endDate || null,
        amountCents,
        autoRenew,
        billingContactEmail: billingEmail.trim() || null,
        notes: notes.trim() || null,
      });
      setContract({ ...contract, ...updated, rentals: contract.rentals });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const refreshContract = async () => {
    if (!id) return;
    const fresh = await adContractsApi.get(id);
    setContract(fresh);
  };

  const openAttach = async () => {
    if (!contract) return;
    setShowAttach(true);
    try {
      const list = await adContractsApi.unattachedRentals(contract.device_id);
      setAvailable(list);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const attach = async (rentalId: string) => {
    if (!contract) return;
    setAttachBusy(rentalId);
    setErr(null);
    try {
      await adContractsApi.attachRental(contract.id, rentalId);
      // Refresh both the contract (to show the new rental in the list) and
      // the picker (to remove the now-attached rental from the available pool).
      setAvailable((prev) => prev.filter((r) => r.id !== rentalId));
      await refreshContract();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setAttachBusy(null);
    }
  };

  const openMediaPicker = async () => {
    if (!contract) return;
    setReplaceTargetRentalId(null); // explicit: this is attach mode
    setShowMediaPicker(true);
    setMediaSearch('');
    // Seed the picker dates with the contract window so the common case
    // (ad runs the whole contract) is one click. Admin can change them
    // for pre-programmed ads with a narrower window.
    setPickerStart(toDateInput(contract.start_date));
    setPickerEnd(toDateInput(contract.end_date));
    try {
      const list = await mediaApi.list();
      setMediaLib(list);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const openReplacePicker = async (rentalId: string) => {
    if (!contract) return;
    setReplaceTargetRentalId(rentalId);
    setShowMediaPicker(true);
    setMediaSearch('');
    // Dates aren't used in replace mode but clear them so the panel
    // collapses visually (and so they don't get sent if user toggles
    // back to attach mode).
    setPickerStart('');
    setPickerEnd('');
    try {
      const list = await mediaApi.list();
      setMediaLib(list);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const attachMedia = async (mediaId: string) => {
    if (!contract) return;
    setMediaBusy(mediaId);
    setErr(null);
    try {
      if (replaceTargetRentalId) {
        // In-place swap: same rental row, new media_id. Preserves dates,
        // contract link, payment history; republishes to VNNOX.
        const result = await rentalsApi.replaceMedia(replaceTargetRentalId, mediaId);
        if (result.publishError) {
          setErr(`Artwork swapped, but VNNOX publish failed: ${result.publishError}. Use Republish from the device page to retry.`);
        }
        // Close on success — replace is a one-shot action, no need to
        // keep the picker open like attribution batching.
        setShowMediaPicker(false);
        setReplaceTargetRentalId(null);
      } else {
        // Attribution mode: create a fresh rental for the contract.
        await adContractsApi.attachMedia(contract.id, {
          mediaId,
          // Stamp the rental row with the company name (falling back to the
          // contact name) so it reads correctly on pages that don't do the
          // contract->client lookup.
          advertiserName: client?.company || client?.name || undefined,
          startDate: pickerStart || undefined,
          endDate: pickerEnd || undefined,
        });
        // Don't close — admin may want to add several files in a row.
      }
      await refreshContract();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setMediaBusy(null);
    }
  };

  const startEditRental = (r: { id: string; start_date: string | null; end_date: string | null }) => {
    setEditingRental(r.id);
    setEditStart(toDateInput(r.start_date));
    setEditEnd(toDateInput(r.end_date));
  };

  const saveRentalDates = async () => {
    if (!editingRental) return;
    setBusy(true);
    setErr(null);
    try {
      await rentalsApi.reschedule(editingRental, {
        startDate: editStart || undefined,
        endDate:   editEnd   || undefined,
      });
      setEditingRental(null);
      await refreshContract();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const resendActivation = async () => {
    if (!contract) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await adContractsApi.sendActivation(contract.id);
      // Surface what happened so admin understands the result. shop-api
      // returns soft-success even when no email was sent (already-active
      // or no-email-on-file), so we can't just say "Sent!" blindly.
      if (r.sent) {
        alert('Activation email sent. Ask them to check their inbox (and spam).');
      } else if (r.alreadyActive) {
        alert('That client is already activated — they should use holmgraphics.ca/shop/login (Forgot password if they lost it), not the activation link.');
      } else if (!r.hasEmail) {
        alert('No email on file for this client in shop-api. Add an email to their client record and try again.');
      } else {
        alert('shop-api accepted the request but reported no email was sent. Check the shop-api logs.');
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const detach = async (rentalId: string) => {
    if (!contract) return;
    if (!confirm('Unlink this ad from the contract? The ad stays on the screen, but it won\'t be attributed to this client anymore.')) return;
    setBusy(true);
    setErr(null);
    try {
      await adContractsApi.detachRental(contract.id, rentalId);
      await refreshContract();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!confirm('Cancel this contract? Rentals stay attached for history.')) return;
    setBusy(true);
    setErr(null);
    try {
      await adContractsApi.cancel(contract.id);
      navigate(`/devices/${contract.device_id}`);
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };

  const statusColor =
    contract.status === 'active'    ? '#dcfce7' :
    contract.status === 'expired'   ? '#fef3c7' :
                                       '#fee2e2';

  return (
    <div className="stack">
      <div className="row between">
        <div>
          <h2 style={{ margin: '0 0 4px 0' }}>
            Ad contract <span className="muted" style={{ fontSize: 14 }}>#{contract.id.slice(0, 8)}</span>
          </h2>
          <div className="muted" style={{ fontSize: 14 }}>
            <strong style={{ color: 'var(--text)' }}>
              {client?.company || client?.name || `Client #${contract.client_id}`}
            </strong>
            {client?.company && client.name && client.name !== client.company && ` · ${client.name}`}
            {' · '}
            <Link to={`/devices/${contract.device_id}`}>
              {contract.device_name ?? contract.device_id.slice(0, 8)}
            </Link>
            {contract.device_location && ` · ${contract.device_location}`}
          </div>
          <div style={{ marginTop: 6 }}>
            <button
              className="secondary"
              onClick={resendActivation}
              disabled={busy}
              style={{ fontSize: 12, padding: '2px 8px' }}
              title="Re-send the customer activation email so the client can log in to /advertise/my-ads"
            >
              Send activation email
            </button>
            {client?.email && (
              <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
                will go to {client.email}
              </span>
            )}
          </div>
        </div>
        <span
          style={{
            fontSize: 13,
            padding: '4px 12px',
            borderRadius: 4,
            background: statusColor,
            alignSelf: 'flex-start',
          }}
        >
          {contract.status}{isOwnerPerpetual && ' · owner-perpetual'}
        </span>
      </div>

      {err && <div className="error-banner">{err}</div>}

      {/* --- Term + billing card --- */}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Term &amp; billing</h3>

        {isOwnerPerpetual && (
          <div className="muted" style={{ fontSize: 13, marginBottom: 12, padding: 8, background: '#f3f4f6', borderRadius: 4 }}>
            This is an owner-perpetual contract — created automatically when the
            screen was assigned to this client. Term and billing fields can't be
            edited here; clear ownership on the device to retire it.
          </div>
        )}

        <div className="row" style={{ gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label>Start date</label>
            <input value={toDateInput(contract.start_date)} disabled />
          </div>
          <div style={{ flex: 1 }}>
            <label>End date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={isOwnerPerpetual || busy}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label>Term</label>
            <input
              value={contract.term_count && contract.term_unit ? `${contract.term_count} × ${contract.term_unit}` : '—'}
              disabled
            />
          </div>
        </div>

        <div className="row" style={{ gap: 12, marginTop: 8 }}>
          <div style={{ flex: 1 }}>
            <label>Amount ({contract.currency})</label>
            <input
              value={amountDollars}
              onChange={(e) => setAmountDollars(e.target.value)}
              inputMode="decimal"
              disabled={isOwnerPerpetual || busy}
              placeholder={isOwnerPerpetual ? '—' : '1200.00'}
            />
          </div>
          <div style={{ flex: 2 }}>
            <label>Billing contact email (overrides client default)</label>
            <input
              value={billingEmail}
              onChange={(e) => setBillingEmail(e.target.value)}
              disabled={isOwnerPerpetual || busy}
              placeholder="accounts@client.com"
            />
          </div>
        </div>

        <div style={{ marginTop: 8 }}>
          <label>Notes</label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isOwnerPerpetual || busy}
          />
        </div>

        <label className="row" style={{ gap: 8, marginTop: 12, fontSize: 14 }}>
          <input
            type="checkbox"
            checked={autoRenew}
            onChange={(e) => setAutoRenew(e.target.checked)}
            disabled={isOwnerPerpetual || busy}
            style={{ width: 'auto' }}
          />
          <span>
            Auto-renew — mints a QBO invoice ~30 days before <code>end_date</code>.{' '}
            <span className="muted" style={{ fontSize: 12 }}>
              (Dormant until the server-side master switch{' '}
              <code>RENEWAL_AUTO_INVOICE</code> is flipped to <code>true</code>.)
            </span>
          </span>
        </label>

        <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          {!isOwnerPerpetual && (
            <button onClick={cancel} disabled={busy} style={{ background: '#fee2e2', color: '#dc2626' }}>
              Cancel contract
            </button>
          )}
          <button onClick={save} disabled={busy || isOwnerPerpetual}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* --- Renewal status --- */}
      {!isOwnerPerpetual && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Renewal status</h3>
          {contract.renewal_invoiced_at ? (
            <div>
              <div>
                Invoice minted on{' '}
                <strong>{new Date(contract.renewal_invoiced_at).toLocaleDateString()}</strong>
              </div>
              {contract.renewal_invoice_id && (
                <div className="muted" style={{ fontSize: 13 }}>
                  QBO Invoice ID: <code>{contract.renewal_invoice_id}</code>
                </div>
              )}
            </div>
          ) : contract.auto_renew && contract.end_date ? (
            <div className="muted" style={{ fontSize: 14 }}>
              Will invoice when within 30 days of {contract.end_date} —{' '}
              <em>contingent on master switch.</em>
            </div>
          ) : (
            <div className="muted" style={{ fontSize: 14 }}>
              Auto-renew off — no invoice will be generated.
            </div>
          )}
        </div>
      )}

      {/* --- Ads (rentals) under this contract --- */}
      <div className="card">
        <div className="row between" style={{ marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>Ads under this contract</h3>
          <div className="row" style={{ gap: 8 }}>
            <button onClick={openMediaPicker} disabled={busy} title="Pick a file from the media library and link it to this contract (use this for ads not booked via /advertise)">
              + Add from media library
            </button>
            <button onClick={openAttach} disabled={busy} title="Attach a rental that was booked via /advertise but not yet attributed">
              + Attach existing ad
            </button>
          </div>
        </div>
        {!contract.rentals || contract.rentals.length === 0 ? (
          <div className="muted" style={{ fontSize: 13 }}>No ads attached yet.</div>
        ) : (
          <table style={{ width: '100%', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                <th>Status</th>
                <th>Window</th>
                <th>Daypart</th>
                <th>Amount</th>
                <th>Advertiser</th>
                <th></th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {contract.rentals.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td>{r.status}</td>
                  <td>
                    {editingRental === r.id ? (
                      <div className="row" style={{ gap: 4 }}>
                        <input
                          type="date"
                          value={editStart}
                          onChange={(e) => setEditStart(e.target.value)}
                          style={{ fontSize: 12, padding: '2px 4px', width: 130 }}
                        />
                        <span>→</span>
                        <input
                          type="date"
                          value={editEnd}
                          onChange={(e) => setEditEnd(e.target.value)}
                          style={{ fontSize: 12, padding: '2px 4px', width: 130 }}
                        />
                      </div>
                    ) : r.start_date && r.end_date ? (
                      `${toDateInput(r.start_date)} → ${toDateInput(r.end_date)}`
                    ) : (
                      <span className="muted">unscheduled</span>
                    )}
                  </td>
                  <td>{r.start_time?.slice(0, 5)}–{r.end_time?.slice(0, 5)}</td>
                  <td>${(r.amount_cents / 100).toFixed(2)} {r.currency}</td>
                  <td>{client?.company || client?.name || r.advertiser_name}</td>
                  <td>
                    {r.artwork_url && (
                      <a href={r.artwork_url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
                        artwork
                      </a>
                    )}
                  </td>
                  <td>
                    <Link to={`/rentals/${r.id}`} style={{ fontSize: 13 }}>Open →</Link>
                  </td>
                  <td>
                    {editingRental === r.id ? (
                      <div className="row" style={{ gap: 4 }}>
                        <button onClick={saveRentalDates} disabled={busy} style={{ fontSize: 12, padding: '2px 8px' }}>Save</button>
                        <button onClick={() => setEditingRental(null)} disabled={busy} style={{ fontSize: 12, padding: '2px 8px' }}>Cancel</button>
                      </div>
                    ) : (
                      <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => openReplacePicker(r.id)}
                          disabled={busy}
                          style={{ fontSize: 12, padding: '2px 8px' }}
                          title="Swap the playing creative in place. Dates and contract link stay the same."
                        >
                          Replace artwork
                        </button>
                        <button
                          onClick={() => startEditRental(r)}
                          disabled={busy}
                          style={{ fontSize: 12, padding: '2px 8px' }}
                          title="Re-schedule this ad's run window"
                        >
                          Edit dates
                        </button>
                        <button
                          onClick={() => detach(r.id)}
                          disabled={busy}
                          style={{ fontSize: 12, padding: '2px 8px' }}
                          title="Unlink from this contract (the ad keeps running, just isn't attributed)"
                        >
                          Detach
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* --- Attach from media library picker --- */}
      {showMediaPicker && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => setShowMediaPicker(false)}
        >
          <div
            className="card"
            style={{ maxWidth: 720, width: '90%', maxHeight: '90vh', overflowY: 'auto', background: 'white' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="row between" style={{ marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>
                {replaceTargetRentalId ? 'Replace artwork' : 'Add ad from media library'}
              </h3>
              <button onClick={() => { setShowMediaPicker(false); setReplaceTargetRentalId(null); }}>✕</button>
            </div>
            {err && <div className="error-banner" style={{ marginBottom: 8 }}>{err}</div>}
            <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
              {replaceTargetRentalId ? (
                <>
                  Pick the new file. The rental's run window, contract link, and
                  status stay the same — only the playing creative changes. We'll
                  republish to VNNOX automatically (if the ad is currently live)
                  and mirror the new file into the client's <code>LED Ads</code> folder.
                </>
              ) : (
                <>
                  Pick a file you've already uploaded to the media library — typical for
                  ads the screen owner provided directly. Each pick creates a rental row
                  linked to this contract and (best-effort) mirrors the file into the
                  client's <code>LED Ads</code> folder.
                </>
              )}
            </div>

            {/* Pre-program: the dates applied to whatever you attach next. Hidden in replace mode. */}
            {!replaceTargetRentalId && (
              <div style={{ padding: 12, background: '#f9fafb', border: '1px solid #ddd', borderRadius: 4, marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Run window for next attach</div>
                <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                  Leave at the contract window for ads that run the whole term, or
                  narrow them to pre-program a future ad. You can change these between
                  clicks if you're queueing several ads with different start dates.
                </div>
                <div className="row" style={{ gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12 }}>Start date</label>
                    <input type="date" value={pickerStart} onChange={(e) => setPickerStart(e.target.value)} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12 }}>End date</label>
                    <input type="date" value={pickerEnd} onChange={(e) => setPickerEnd(e.target.value)} />
                  </div>
                  <button
                    onClick={() => {
                      setPickerStart(toDateInput(contract.start_date));
                      setPickerEnd(toDateInput(contract.end_date));
                    }}
                    style={{ alignSelf: 'flex-end', fontSize: 12 }}
                    title="Reset to the contract's full term"
                  >
                    Use contract window
                  </button>
                </div>
              </div>
            )}

            <input
              type="text"
              placeholder="Filter by filename…"
              value={mediaSearch}
              onChange={(e) => setMediaSearch(e.target.value)}
              style={{ marginBottom: 12 }}
            />
            {mediaLib.length === 0 ? (
              <div className="muted">No media in the library yet. Upload via the Media page first.</div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                gap: 12,
              }}>
                {mediaLib
                  .filter((m) => {
                    if (!mediaSearch.trim()) return true;
                    const needle = mediaSearch.toLowerCase();
                    return (
                      m.original_name.toLowerCase().includes(needle) ||
                      m.filename.toLowerCase().includes(needle)
                    );
                  })
                  .map((m) => {
                    const isImage = m.mime_type.startsWith('image/');
                    const preview = m.thumbnail_url || (isImage ? m.storage_url : null);
                    return (
                      <div key={m.id} style={{ border: '1px solid #ddd', borderRadius: 4, padding: 8 }}>
                        <div style={{
                          width: '100%',
                          height: 100,
                          background: '#f3f4f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          marginBottom: 6,
                        }}>
                          {preview ? (
                            <img
                              src={preview}
                              alt=""
                              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            />
                          ) : (
                            <span className="muted" style={{ fontSize: 11 }}>{m.mime_type}</span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, marginBottom: 6, wordBreak: 'break-word' }}>
                          {m.original_name}
                        </div>
                        <button
                          onClick={() => attachMedia(m.id)}
                          disabled={mediaBusy !== null}
                          style={{ width: '100%', fontSize: 12 }}
                        >
                          {mediaBusy === m.id ? 'Attaching…' : 'Attach'}
                        </button>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- Attach existing ad picker --- */}
      {showAttach && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => setShowAttach(false)}
        >
          <div
            className="card"
            style={{ maxWidth: 600, width: '90%', maxHeight: '90vh', overflowY: 'auto', background: 'white' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="row between" style={{ marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Attach existing ad</h3>
              <button onClick={() => setShowAttach(false)}>✕</button>
            </div>
            {err && <div className="error-banner" style={{ marginBottom: 8 }}>{err}</div>}
            <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
              Ads on this screen that aren't yet attributed to any contract. Click "Attach" to link an ad to this contract.
            </div>
            {available.length === 0 ? (
              <div>
                <div className="muted" style={{ marginBottom: 8 }}>No unattached rentals on this screen.</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  This picker only shows ads that came through the <code>/advertise</code> booking
                  flow. For files you uploaded yourself via Media or Playlists, close this and use{' '}
                  <strong>+ Add from media library</strong> instead.
                </div>
              </div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {available.map((r) => (
                  <li key={r.id} style={{ padding: '10px 0', borderBottom: '1px solid #eee' }}>
                    <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                      {r.artwork_url && r.artwork_mime?.startsWith('image/') && (
                        <img
                          src={r.artwork_url}
                          alt=""
                          style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }}
                        />
                      )}
                      <div style={{ flex: 1, fontSize: 13 }}>
                        <div><strong>{r.advertiser_name}</strong> {r.advertiser_business && <span className="muted">({r.advertiser_business})</span>}</div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {r.start_date && r.end_date ? `${toDateInput(r.start_date)} → ${toDateInput(r.end_date)}` : <em>unscheduled</em>}
                          {' · '}{r.start_time.slice(0, 5)}–{r.end_time.slice(0, 5)}
                          {' · '}${(r.amount_cents / 100).toFixed(2)} {r.currency}
                          {' · '}<span style={{ fontStyle: 'italic' }}>{r.status}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => attach(r.id)}
                        disabled={attachBusy !== null}
                      >
                        {attachBusy === r.id ? 'Attaching…' : 'Attach'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
